import React, { useEffect, useMemo, useRef, useState } from 'react';

const DEMO_EMAIL = 'demo@vaisptech.com';
const DEMO_PASSWORD = 'demo123';
const STORAGE_KEY = 'visptech_admin_demo';

const AdminSection = ({ forceOpen = false }) => {
  const [open, setOpen] = useState(forceOpen);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [authed, setAuthed] = useState(false);
  const emailRef = useRef(null);

  const teamMembers = useMemo(() => (
    [
      { id: 1, name: 'Mohit Jangra', role: 'Administrator', email: 'mohit@visptech.com' },
      { id: 2, name: 'Aarav Sharma', role: 'Project Manager', email: 'aarav@visptech.com' },
      { id: 3, name: 'Deepak Kumar', role: 'Frontend Engineer', email: 'deepakkumar@visptech.com' },
      { id: 4, name: 'Rohan Mehta', role: 'Backend Engineer', email: 'rohan@visptech.com' }
    ]
  ), []);

  const clientList = useMemo(() => (
    [
      { id: 1, name: 'Acme Corp', contact: 'jane.doe@acme.com', status: 'Active', budget: '$50,000' },
      { id: 2, name: 'Globex', contact: 'it@globex.com', status: 'Onboarding', budget: '$20,000' },
      { id: 3, name: 'Initech', contact: 'peter@initech.com', status: 'Pending', budget: '$15,000' },
      { id: 4, name: 'Umbrella Co', contact: 'ops@umbrella.co', status: 'Active', budget: '$35,000' }
    ]
  ), []);

  const storedUser = useMemo(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (storedUser && storedUser.email) {
      setAuthed(true);
    }
  }, [storedUser]);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      setTimeout(() => emailRef.current && emailRef.current.focus(), 100);
    }
  }, [forceOpen]);

  useEffect(() => {
    if (forceOpen) return;
    const openFromHash = () => {
      if (window.location.hash === '#admin') {
        setOpen(true);
        setTimeout(() => emailRef.current && emailRef.current.focus(), 100);
      }
    };
    openFromHash();
    const handleOpen = () => {
      setOpen(true);
      setTimeout(() => emailRef.current && emailRef.current.focus(), 100);
    };
    window.addEventListener('hashchange', openFromHash);
    window.addEventListener('open-admin', handleOpen);
    return () => {
      window.removeEventListener('hashchange', openFromHash);
      window.removeEventListener('open-admin', handleOpen);
    };
  }, [forceOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage('');

    if (email !== DEMO_EMAIL || password !== DEMO_PASSWORD) {
      setMessage('Invalid credentials. Use demo@visptech.com / demo123');
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify({ email, ts: Date.now() }));
    setAuthed(true);
    setMessage('Login successful.');
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setMessage('Logged out.');
  };

  return (
    <section id="admin" className="relative px-6 z-10">
      <div className={`max-w-5xl mx-auto transition-all duration-500 ${(open || forceOpen) ? 'opacity-100 translate-y-0 py-20' : 'opacity-0 -translate-y-2 max-h-0 overflow-hidden py-0'}`}>
        {!authed ? (
          <div className="max-w-md mx-auto">
            <h2 className="text-3xl font-bold text-center mb-6 text-white">Admin Login</h2>
            <p className="text-center text-gray-300 mb-8">Demo access only. Use the credentials shown below.</p>
            <form onSubmit={handleSubmit} className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-6 shadow-xl">
              <div className="mb-4">
                <label htmlFor="admin-email" className="block text-sm font-medium text-gray-200 mb-2">Email</label>
                <input
                  ref={emailRef}
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  autoComplete="username"
                  required
                />
              </div>
              <div className="mb-6">
                <label htmlFor="admin-password" className="block text-sm font-medium text-gray-200 mb-2">Password</label>
                <input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  autoComplete="current-password"
                  required
                  minLength={6}
                />
              </div>
              <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors">Login</button>
              {message && <p className="mt-4 text-center text-sm text-gray-300">{message}</p>}
            </form>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-white">Admin Dashboard</h2>
              <button onClick={handleLogout} className="bg-transparent border-2 border-gray-600 hover:border-white text-white px-4 py-2 rounded-lg font-semibold transition-colors">Logout</button>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              <div className="p-6 rounded-xl bg-gray-800/40 border border-gray-700/50">
                <h3 className="text-sm text-gray-400">Active Projects</h3>
                <p className="mt-2 text-3xl font-bold text-white">8</p>
              </div>
              <div className="p-6 rounded-xl bg-gray-800/40 border border-gray-700/50">
                <h3 className="text-sm text-gray-400">New Leads</h3>
                <p className="mt-2 text-3xl font-bold text-white">23</p>
              </div>
              <div className="p-6 rounded-xl bg-gray-800/40 border border-gray-700/50">
                <h3 className="text-sm text-gray-400">Support Tickets</h3>
                <p className="mt-2 text-3xl font-bold text-white">5</p>
              </div>
              <div className="p-6 rounded-xl bg-gray-800/40 border border-gray-700/50">
                <h3 className="text-sm text-gray-400">Team Members</h3>
                <p className="mt-2 text-3xl font-bold text-white">{teamMembers.length}</p>
              </div>
              <div className="p-6 rounded-xl bg-gray-800/40 border border-gray-700/50">
                <h3 className="text-sm text-gray-400">Active Clients</h3>
                <p className="mt-2 text-3xl font-bold text-white">{clientList.filter(c => c.status === 'Active').length}</p>
              </div>
              <div className="p-6 rounded-xl bg-gray-800/40 border border-gray-700/50">
                <h3 className="text-sm text-gray-400">Pending Invoices</h3>
                <p className="mt-2 text-3xl font-bold text-white">3</p>
              </div>
            </div>

            <div className="mt-8 p-6 rounded-xl bg-gray-800/40 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white">Welcome</h3>
              <p className="mt-2 text-gray-300">Logged in as {storedUser?.email || email}. This is a demo-only admin area. Replace with real data and actions as needed.</p>
            </div>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <div className="p-6 rounded-xl bg-gray-800/40 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-white">Team Members</h3>
                <ul className="mt-4 divide-y divide-gray-800/60">
                  {teamMembers.map(m => (
                    <li key={m.id} className="py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-indigo-600/30 border border-indigo-500/50 flex items-center justify-center text-sm font-semibold text-indigo-200">
                          {m.name.split(' ').map(n => n[0]).join('').slice(0,2)}
                        </div>
                        <div>
                          <p className="text-white font-medium leading-none">{m.name}</p>
                          <p className="text-gray-400 text-sm mt-1">{m.role}</p>
                        </div>
                      </div>
                      <a href={`mailto:${m.email}`} className="text-indigo-400 hover:text-indigo-300 text-sm">{m.email}</a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 rounded-xl bg-gray-800/40 border border-gray-700/50 overflow-x-auto">
                <h3 className="text-lg font-semibold text-white">Client Details</h3>
                <table className="mt-4 w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400">
                      <th className="py-2 pr-4 font-normal">Client</th>
                      <th className="py-2 pr-4 font-normal">Contact</th>
                      <th className="py-2 pr-4 font-normal">Status</th>
                      <th className="py-2 font-normal">Budget</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/60">
                    {clientList.map(c => (
                      <tr key={c.id} className="text-gray-300">
                        <td className="py-3 pr-4 text-white">{c.name}</td>
                        <td className="py-3 pr-4"><a href={`mailto:${c.contact}`} className="text-indigo-400 hover:text-indigo-300">{c.contact}</a></td>
                        <td className="py-3 pr-4">
                          <span className={`px-2 py-1 rounded-md text-xs ${c.status === 'Active' ? 'bg-green-500/20 text-green-300 border border-green-500/40' : c.status === 'Onboarding' ? 'bg-yellow-500/20 text-yellow-200 border border-yellow-500/40' : 'bg-gray-500/20 text-gray-300 border border-gray-500/40'}`}>{c.status}</span>
                        </td>
                        <td className="py-3">{c.budget}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminSection;
